import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Lock, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, isAuthenticated, checking } = useAdminAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!checking && isAuthenticated) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [checking, isAuthenticated, navigate]);

  useEffect(() => {
    document.title = "Admin Login – Rydey";
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await login(password);
      toast.success("Welcome back, admin");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : err?.response?.status === 429
          ? "Too many attempts. Please try again later."
          : "Login failed. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1B40] flex items-center justify-center px-5" data-testid="admin-login-page">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />
      <div
        className="absolute -right-40 -top-40 w-[480px] h-[480px] rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #F5A623 0%, transparent 60%)" }}
      />

      <div className="relative w-full max-w-md">
        <a
          href="/"
          className="inline-flex items-center bg-white rounded-lg px-3 py-2 mb-7 shadow-md"
          data-testid="admin-login-logo"
        >
          <img src="/brand/rydey-logo.png" alt="Rydey" className="h-9 w-auto" />
        </a>

        <div className="bg-white rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-lg bg-[#0D1B40] text-[#F5A623] flex items-center justify-center">
              <ShieldCheck size={22} />
            </div>
            <div>
              <div className="text-[11px] tracking-[0.18em] font-bold text-[#F5A623] uppercase">
                Admin Console
              </div>
              <h1 className="text-[#0D1B40] text-[22px] font-extrabold leading-tight">
                Sign in to continue
              </h1>
            </div>
          </div>
          <p className="text-slate-500 text-[14px] leading-relaxed mt-3 mb-6">
            Enter the admin password to view and manage all incoming corporate quote
            requests.
          </p>

          <form onSubmit={onSubmit} data-testid="admin-login-form">
            <label className="field-label" htmlFor="admin-password">
              Admin Password
            </label>
            <div className="relative">
              <Lock
                size={17}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                id="admin-password"
                type="password"
                autoFocus
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field-input pl-11"
                placeholder="••••••••••••••"
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <div
                className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px]"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="mt-5 w-full btn-primary justify-center disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Signing in...
                </>
              ) : (
                <>
                  Sign In <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <a
            href="/"
            className="mt-6 block text-center text-[13px] text-slate-500 hover:text-[#0D1B40] transition-colors"
            data-testid="admin-back-home"
          >
            ← Back to website
          </a>
        </div>

        <p className="mt-6 text-center text-white/40 text-[12px]">
          Protected area · Sessions expire after 12 hours
        </p>
      </div>
    </div>
  );
}
